import type { PublicStatusResponse, PublicStatusState } from '@nadoutong/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PanelError } from '../../components/PanelError.tsx';
import { Skeleton } from '../../components/Skeleton.tsx';
import { formatTimestamp } from '../../components/formatters.ts';
import { STATE_TONE, stateText } from '../../components/publicStatus.ts';
import { useT } from '../../i18n/index.ts';
import { Chip, Panel, StatusLamp, buttonClass } from '../../ui/index.ts';

type TranslateFn = ReturnType<typeof useT>;

function overallStateText(state: PublicStatusState, t: TranslateFn): string {
  switch (state) {
    case 'healthy':
      return t('dashboard.status.overall.healthy');
    case 'busy':
      return t('dashboard.status.overall.busy');
    case 'unavailable':
      return t('dashboard.status.overall.unavailable');
  }
}

/**
 * Service-status strip — the glance-pace answer to "is it up?", with
 * the full timeline a click away at `/status`.
 *
 * Namespaces are the whole vocabulary here, exactly as on the `/status`
 * page: a payload without `namespaces[]` gets an empty state, never a
 * downgraded channel-era vendor view. That cut is already made in
 * `pages/Status.tsx` (see its header for the reasoning and the
 * precedents); rendering vendors on the dashboard would reintroduce a
 * vocabulary this console no longer speaks.
 */
export function StatusStrip(props: {
  readonly statusQ: UseQueryResult<PublicStatusResponse>;
}): JSX.Element {
  const t = useT();
  const status = props.statusQ.data;
  const namespaces = status?.namespaces ?? [];
  return (
    <Panel
      caption={t('dashboard.status.caption')}
      padded
      testId="dashboard-status"
      actions={
        <Link
          to="/status"
          className={buttonClass('ghost', 'sm')}
          data-testid="dashboard-status-link"
        >
          {t('dashboard.status.view')}
        </Link>
      }
    >
      {props.statusQ.isLoading ? (
        <Skeleton className="h-8" testId="dashboard-status-loading" />
      ) : props.statusQ.isError ? (
        <PanelError
          message={t('dashboard.status.error')}
          retryLabel={t('dashboard.retry')}
          onRetry={() => {
            void props.statusQ.refetch();
          }}
          testId="dashboard-status-error"
        />
      ) : status !== undefined ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {/* The ONE live element on this page — pulse is reserved for it. */}
          <span
            className="inline-flex items-center gap-2 text-body font-medium text-ink"
            data-testid="dashboard-status-overall"
          >
            <StatusLamp
              tone={STATE_TONE[status.summary.state]}
              pulse
              testId="dashboard-status-lamp"
            />
            {overallStateText(status.summary.state, t)}
          </span>
          {namespaces.length > 0 ? (
            namespaces.map((namespace) => (
              <Chip
                key={namespace.namespace_id}
                tone={STATE_TONE[namespace.state ?? 'healthy']}
                testId={`dashboard-status-namespace-${namespace.namespace_slug}`}
              >
                {namespace.namespace_display_name}
                {/*
                 * The chip's tone is the only VISIBLE carrier of the
                 * state, and its lamp is `aria-hidden` decoration: a
                 * screen reader would otherwise hear a bare list of
                 * namespace names and could not tell which one is down
                 * (WCAG 1.4.1). Same three words `/status` prints beside
                 * each namespace lamp — one `stateText`, one vocabulary.
                 */}
                <span className="sr-only">{` ${stateText(namespace.state ?? 'healthy', t)}`}</span>
              </Chip>
            ))
          ) : (
            <p className="m-0 text-caption text-ink-low">{t('dashboard.status.empty')}</p>
          )}
          <span className="font-mono text-caption text-ink-low sm:ml-auto">
            {t('dashboard.status.updated', {
              timestamp: formatTimestamp(status.updated_at),
            })}
          </span>
        </div>
      ) : (
        /*
         * `!isLoading && !isError && data === undefined` — the corner
         * React Query produces when the query is PAUSED (offline), not
         * fetching. `pages/Status.tsx` renders a skeleton for the same
         * corner of the same query; a strip with a caption, a link and
         * no body would be this page telling a different story about
         * one state.
         */
        <Skeleton className="h-8" testId="dashboard-status-pending" />
      )}
    </Panel>
  );
}
